import {
  useGetCyclesQuery,
  useGetHourlyMetricsQuery,
  useGetHourlyPositionsQuery,
  useGetAllocationPerformanceQuery,
  useGetGeminiGatekeeperStatsQuery,
  useGetDcaRecentFillsQuery,
  useGetDcaUniverseChangesQuery,
} from "@/store/api";
import { useAppSelector } from "@/store";
import UserPerformance from "@/routes/UserPerformance";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { PnLPill } from "@/components/domain/PnLPill";
import { StatusPill } from "@/components/domain/StatusPill";
import { fmtUsd, fmtTime, fmtNum } from "@/lib/format";
import { EquityCurve } from "@/components/charts/EquityCurve";
import { DailyPnL } from "@/components/charts/DailyPnL";
import { CycleHeatmap } from "@/components/charts/CycleHeatmap";

export default function Performance() {
  const role = useAppSelector((s) => s.auth.user?.role);
  if (role !== "admin") return <UserPerformance />;
  return <AdminPerformance />;
}

const verdictTone: Record<string, "good"|"warn"|"bad"|"muted"> = {
  APPROVE: "good", REJECT: "bad", NEEDS_REVIEW: "warn",
  ADD: "good", DROP: "bad", PAUSE: "warn", RESUME: "info" as any,
};

const sleeveLabels: Record<string, string> = {
  pre_pump_auto: "Pre-pump auto",
  alpha_bsc_auto: "Alpha BSC",
  conviction_dca: "Conviction DCA",
  grid_trader: "Grid trader",
};

function AdminPerformance() {
  const cycles = useGetCyclesQuery();
  const hourlyMetrics = useGetHourlyMetricsQuery(50, { pollingInterval: 60000 });
  const closedHourly = useGetHourlyPositionsQuery(
    { status: "closed", limit: 200 }, { pollingInterval: 60000 });
  const enginePerf = useGetAllocationPerformanceQuery(30, { pollingInterval: 60000 });
  const gkStats = useGetGeminiGatekeeperStatsQuery(7, { pollingInterval: 60000 });
  const dcaFills = useGetDcaRecentFillsQuery({ days: 7, limit: 50 }, { pollingInterval: 60000 });
  const universeChanges = useGetDcaUniverseChangesQuery(20, { pollingInterval: 120000 });

  const hm = hourlyMetrics.data;
  const hourlyClosedRows: any[] = closedHourly.data ?? [];

  // Multi-engine P&L tiles
  const sleeves = (enginePerf.data?.sleeves || {}) as Record<string, { trades: number; realized_pnl: number }>;
  const totalRealized = Number(enginePerf.data?.total_realized_pnl_usd || 0);

  // Gatekeeper stats
  const gk = gkStats.data || {};
  const byDecision = gk.by_decision || {};
  const totalCalls = Number(gk.total_calls || 0);
  const approves = Number(byDecision.APPROVE?.count || 0);
  const rejects = Number(byDecision.REJECT?.count || 0);
  const reviews = Number(byDecision.NEEDS_REVIEW?.count || 0);
  const rejectedNotional = Number(byDecision.REJECT?.notional_usd || 0);
  const approveRate = totalCalls > 0 ? (approves / totalCalls * 100) : 0;

  return (
    <div className="space-y-4">

      {/* ====== Multi-engine 30d P&L ====== */}
      <Card>
        <CardHeader>
          <CardTitle>
            Multi-engine P&L (30 days)
            <span className="text-xs font-normal text-muted ml-2">
              realized only — paper grid + open positions not included
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {enginePerf.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Tile label="TOTAL"
                    value={`${totalRealized >= 0 ? "+" : ""}${fmtUsd(totalRealized)}`}
                    tone={totalRealized > 0 ? "good" : totalRealized < 0 ? "bad" : "muted"} />
              {Object.entries(sleeves).map(([k, v]) => (
                <Tile key={k}
                      label={sleeveLabels[k] || k}
                      value={`${v.realized_pnl >= 0 ? "+" : ""}${fmtUsd(v.realized_pnl)}`}
                      sub={`${v.trades} trades`}
                      tone={v.realized_pnl > 0 ? "good" : v.realized_pnl < 0 ? "bad" : "muted"} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ====== Gemini Gatekeeper (last 7d) ====== */}
      <Card>
        <CardHeader>
          <CardTitle>
            Gemini Gatekeeper — last 7 days
            <span className="text-xs font-normal text-muted ml-2">
              every BUY/SELL goes through Gemini approval
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {gkStats.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Tile label="Total calls" value={String(totalCalls)} sub={`${gk.bypassed_count ?? 0} bypassed (catastrophe)`} />
                <Tile label="APPROVE" value={String(approves)} sub={`${approveRate.toFixed(0)}%`} tone="good" />
                <Tile label="REJECT" value={String(rejects)} sub={fmtUsd(rejectedNotional) + " saved"} tone="bad" />
                <Tile label="NEEDS_REVIEW" value={String(reviews)} tone="warn" />
                <Tile label="Avg APPROVE size" value={approves > 0 ? fmtUsd((byDecision.APPROVE?.notional_usd || 0) / approves) : "—"} />
              </div>
              {gk.recent_rejections && gk.recent_rejections.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted mb-1 mt-2">
                    Recent rejections (last 30)
                  </div>
                  <div className="overflow-x-auto max-h-72 overflow-y-auto border border-border rounded">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-bg2">
                        <tr className="text-left text-muted">
                          <th className="py-1 px-2">Time</th>
                          <th className="py-1 px-2">Action</th>
                          <th className="py-1 px-2">Symbol</th>
                          <th className="py-1 px-2">Source</th>
                          <th className="py-1 px-2 text-right">Size</th>
                          <th className="py-1 px-2">Decision</th>
                          <th className="py-1 px-2">Reasoning</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gk.recent_rejections.map((r: any, i: number) => (
                          <tr key={i} className="border-t border-border/40">
                            <td className="py-1 px-2 text-muted">{(r.ts || "").slice(5, 19).replace("T", " ")}</td>
                            <td className="py-1 px-2">{r.action}</td>
                            <td className="py-1 px-2 font-mono">{r.symbol}</td>
                            <td className="py-1 px-2 text-xs text-muted">{r.source}</td>
                            <td className="py-1 px-2 text-right tabular">{fmtUsd(r.size_usd)}</td>
                            <td className="py-1 px-2">
                              <Badge tone={verdictTone[r.decision] || "muted"}>
                                {r.decision} {r.confidence}%
                              </Badge>
                            </td>
                            <td className="py-1 px-2 text-xs">{(r.reasoning || "").slice(0, 120)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ====== Intelligent DCA recent fills ====== */}
      <Card>
        <CardHeader>
          <CardTitle>
            Intelligent DCA — recent fills (last 7d)
            <span className="text-xs font-normal text-muted ml-2">
              per-coin Gemini sizing multiplier vs equal-split base
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dcaFills.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (dcaFills.data?.fills || []).length === 0 ? (
            <div className="text-sm text-muted">No DCA fills in window.</div>
          ) : (
            <div className="overflow-x-auto max-h-80 overflow-y-auto border border-border rounded">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-bg2">
                  <tr className="text-left text-muted">
                    <th className="py-1 px-2">Time</th>
                    <th className="py-1 px-2">Symbol</th>
                    <th className="py-1 px-2 text-right">Size</th>
                    <th className="py-1 px-2 text-right">Base</th>
                    <th className="py-1 px-2 text-center">Mult</th>
                    <th className="py-1 px-2">Action</th>
                    <th className="py-1 px-2 text-right">Price</th>
                    <th className="py-1 px-2 text-right">Qty</th>
                    <th className="py-1 px-2">Why</th>
                  </tr>
                </thead>
                <tbody>
                  {(dcaFills.data?.fills || []).map((f: any, i: number) => {
                    const mult = Number(f.size_multiplier ?? 1);
                    const tone = mult >= 1.5 ? "good" : mult <= 0.5 ? "bad" : "muted";
                    return (
                      <tr key={i} className="border-t border-border/40">
                        <td className="py-1 px-2 text-muted">{(f.executed_at || "").slice(5, 19).replace("T", " ")}</td>
                        <td className="py-1 px-2 font-mono">{f.symbol}</td>
                        <td className="py-1 px-2 text-right tabular">{fmtUsd(f.size_usd)}</td>
                        <td className="py-1 px-2 text-right tabular text-muted">{fmtUsd(f.base_size_usd ?? 0)}</td>
                        <td className="py-1 px-2 text-center">
                          <Badge tone={tone}>×{mult.toFixed(1)}</Badge>
                        </td>
                        <td className="py-1 px-2">{f.consult_action || "—"}</td>
                        <td className="py-1 px-2 text-right tabular">{fmtNum(+f.avg_price, 6)}</td>
                        <td className="py-1 px-2 text-right tabular">{fmtNum(+f.filled_qty, 4)}</td>
                        <td className="py-1 px-2 text-xs">{(f.consult_reasoning || "").slice(0, 100)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ====== DCA Universe curator changelog ====== */}
      <Card>
        <CardHeader>
          <CardTitle>
            DCA Universe Curator — change log
            <span className="text-xs font-normal text-muted ml-2">
              weekly Gemini-driven ADD/DROP/PAUSE
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {universeChanges.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (universeChanges.data?.changes || []).length === 0 ? (
            <div className="text-sm text-muted">No curator changes yet.</div>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Time</TH>
                  <TH>Action</TH>
                  <TH>Symbol</TH>
                  <TH className="text-right">Conf</TH>
                  <TH>Reasoning</TH>
                  <TH>Trigger</TH>
                </TR>
              </THead>
              <TBody>
                {(universeChanges.data?.changes || []).map((c: any) => (
                  <TR key={c.id}>
                    <TD className="text-xs text-muted">{(c.ts || "").slice(5, 19).replace("T", " ")}</TD>
                    <TD>
                      <Badge tone={verdictTone[c.action] || "muted"}>
                        {c.action}
                      </Badge>
                    </TD>
                    <TD className="font-mono text-sm">{c.symbol}</TD>
                    <TD className="text-right tabular">{c.confidence ?? "—"}%</TD>
                    <TD className="text-xs">{(c.reasoning || "").slice(0, 160)}</TD>
                    <TD className="text-xs text-muted">{c.triggered_by}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ====== Hourly bot section (existing) ====== */}
      <Card>
        <CardHeader>
          <CardTitle>Hourly bot — equity curve</CardTitle>
        </CardHeader>
        <CardContent>
          {closedHourly.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <EquityCurve positions={hourlyClosedRows as any} height={260} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Cycle activity heatmap
            <span className="text-xs font-normal text-muted ml-2">
              hourly bot — UTC times
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {cycles.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <CycleHeatmap cycles={cycles.data ?? []} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Hourly bot — rolling stats
            <span className="text-xs font-normal text-muted ml-2">
              window of {hm?.window ?? 0} closed positions
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hourlyMetrics.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : hm?.insufficient_data ? (
            <p className="text-sm text-muted">
              Need at least 5 closed hourly positions for metrics — currently {hm.window ?? 0}.
            </p>
          ) : (
            <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-4 text-sm">
              <Stat label="Win rate"
                value={hm?.win_rate != null ? `${(hm.win_rate * 100).toFixed(1)}%` : "-"} />
              <Stat label="Sharpe (proxy)" value={String(hm?.sharpe ?? "-")} />
              <Stat label="Max drawdown" value={fmtUsd(hm?.max_drawdown ?? null)} />
              <Stat label="Consecutive losses" value={String(hm?.consecutive_losses ?? 0)} />
              <StatPnL label="Expectancy / trade" value={hm?.expectancy ?? null} />
              <StatPnL label="Avg win" value={hm?.avg_win ?? null} />
              <StatPnL label="Avg loss" value={hm?.avg_loss ?? null} />
              <StatPnL label="Total P&L" value={hm?.total_pnl ?? null} />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Hourly bot — daily P&L last 14 days</CardTitle>
        </CardHeader>
        <CardContent>
          {closedHourly.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <DailyPnL positions={hourlyClosedRows as any} days={14} height={220} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Recent closed hourly positions
            <span className="text-xs font-normal text-muted ml-2">
              {hourlyClosedRows.length} rows
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {closedHourly.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : hourlyClosedRows.length === 0 ? (
            <p className="text-sm text-muted">No closed hourly positions yet.</p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>id</TH>
                  <TH>Symbol</TH>
                  <TH>Status</TH>
                  <TH className="text-right">Entry</TH>
                  <TH className="text-right">Exit</TH>
                  <TH className="text-right">Net P&L</TH>
                  <TH className="text-right">Fees</TH>
                  <TH>Closed</TH>
                </TR>
              </THead>
              <TBody>
                {hourlyClosedRows.map((p) => (
                  <TR key={p.id}>
                    <TD>{p.id}</TD>
                    <TD>{p.symbol}</TD>
                    <TD><StatusPill status={p.status} /></TD>
                    <TD className="text-right tabular">{fmtNum(+p.entry_price, 6)}</TD>
                    <TD className="text-right tabular">
                      {p.exit_price != null ? fmtNum(+p.exit_price, 6) : "-"}
                    </TD>
                    <TD className="text-right">
                      <PnLPill value={p.net_pnl_usd ?? null} />
                    </TD>
                    <TD className="text-right tabular">{fmtUsd(p.fees_paid ?? null)}</TD>
                    <TD className="text-xs text-muted">{fmtTime(p.exit_time ?? null)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  const cls = tone === "good" ? "border-emerald-500/40"
    : tone === "bad" ? "border-rose-500/40"
    : tone === "warn" ? "border-amber-500/40" : "border-border";
  return (
    <div className={`rounded-lg border ${cls} bg-bg2 p-3`}>
      <div className="text-xs text-muted uppercase tracking-wide">{label}</div>
      <div className="text-xl font-semibold tabular">{value}</div>
      {sub && <div className="text-xs text-muted">{sub}</div>}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs text-muted uppercase tracking-wide mb-1">{label}</div>
      <div className="tabular text-lg font-semibold">{value}</div>
    </div>
  );
}

function StatPnL({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs text-muted uppercase tracking-wide mb-1">{label}</div>
      <div className="text-lg font-semibold">
        <PnLPill value={value} />
      </div>
    </div>
  );
}
